import React,{useState,useEffect,useRef} from 'react';
import {useAppContext} from '../context/AppContext';
import {useAuth} from '../context/AuthContext';
import PartCard, {PART_ROW_GRID} from '../components/PartCard';
import {useSearchParams} from 'react-router-dom';
import {api} from '../lib/api';
import {findPartByNumber} from '../lib/partNumbers';
import PartDetailPanel from '../components/PartDetailPanel';
import LimitReachedModal from '../components/Subscription/LimitReachedModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiSearch,FiFilter,FiX,FiLoader,FiTerminal,FiCheck}=FiIcons;

const SearchDashboard=()=> {
  const {parts,totalCount,totalIsApprox,loading,searchQuery,setSearchQuery,filters,setFilters,brandOptions,typeOptions,searchMode,setSearchMode}=useAppContext();
  const {profile} = useAuth();
  const [selectedPart,setSelectedPart]=useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [openedFrom, setOpenedFrom] = useState(null); // part the panel was opened from, for "back"
  const [focusNumber, setFocusNumber] = useState(null);
  const [opening, setOpening] = useState(false);
  const [urlParams, setUrlParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false); // phones only; always visible on lg

  // Links such as /?q=WB13K10026&mode=part pre-fill the search, then clear themselves from the address.
  useEffect(() => {
    const q = urlParams.get('q');
    if (q === null) return;
    const mode = urlParams.get('mode');
    setSearchQuery(q);
    if (['all', 'part', 'model'].includes(mode)) setSearchMode(mode);
    setUrlParams({}, {replace: true});
  }, []);

  const openPart = (part) => {
    setOpenedFrom(null);
    setFocusNumber(null);
    setSelectedPart(part);
  };

  // Clicking a replacement number on a card opens that part. If it isn't in the catalog,
  // open the original part with the number highlighted so the click still does something useful.
  const openNewer = async (part, number) => {
    setOpening(true);
    try {
      const {parts: found} = await api('replacements', {id: part.id});
      const match = findPartByNumber(found || [], number);
      if (match) {
        setOpenedFrom(part);
        setFocusNumber(null);
        setSelectedPart(match);
        return;
      }
    } catch {
      /* fall through to the original part */
    } finally {
      setOpening(false);
    }
    setOpenedFrom(null);
    setFocusNumber(number);
    setSelectedPart(part);
  };

  const isFree = profile?.subscription_status === 'free';
  const remaining = Math.max(0, (profile?.free_searches_limit || 10) - (profile?.free_searches_used || 0));

  const handleFilterChange=(key,value)=> {
    setFilters(prev=> ({...prev,[key]: value}));
  };

  const clearFilters=()=> {
    setFilters({status: 'All',applianceType: 'All',brand: 'All'});
    setSearchQuery('');
  };

  const activeFilterCount=Object.values(filters).filter(v=> v !=='All').length + (searchQuery ? 1 : 0);

  // Trigger paywall if free limit reached
  useEffect(() => {
    if (isFree && remaining === 0 && searchQuery.length > 0) {
      setShowPaywall(true);
    }
  }, [remaining, isFree, searchQuery]);

  const SEARCH_MODES = [
    { value: 'all', label: 'All', label2: 'Part, model or description', placeholder: 'Search 25,000+ parts' },
    { value: 'part', label: 'Part number', label2: 'Part number or cross-reference', placeholder: 'Part number, e.g. WB13K10026' },
    { value: 'model', label: 'Model number', label2: 'Appliance model number', placeholder: 'Model number, e.g. WTW5000DW0' },
  ];
  const activeMode = SEARCH_MODES.find(m => m.value === searchMode) || SEARCH_MODES[0];

  const selectClass = 'w-full min-h-[44px] lg:min-h-0 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 px-3 py-2 text-base lg:text-sm text-slate-900 dark:text-slate-200 focus:border-blue-600 outline-none';

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      <aside className="order-2 lg:order-1 w-full lg:w-60 flex-shrink-0">
        <button
          type="button"
          onClick={() => setFiltersOpen(o => !o)}
          aria-expanded={filtersOpen}
          className="lg:hidden btn-secondary w-full !justify-between"
        >
          <span className="flex items-center gap-2"><SafeIcon icon={FiFilter} className="text-blue-600" /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}</span>
          <span className="text-xs text-slate-500">{filtersOpen ? 'Hide' : 'Show'}</span>
        </button>
        <div className={`${filtersOpen ? 'block' : 'hidden'} lg:block mt-3 lg:mt-0 sticky top-20 space-y-4`}>
          {/* Free tier counter */}
          {isFree && (
            <div className="panel p-4 text-left border-l-[3px] border-l-amber-500">
              <div className="caption mb-2">Free plan usage</div>
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-2xl font-semibold text-slate-900 dark:text-white font-mono">{remaining}</span>
                <span className="text-xs text-slate-500">of 10 searches left</span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-800 h-1.5">
                <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${(remaining / 10) * 100}%` }} />
              </div>
              {remaining <= 2 && (
                <button onClick={() => window.location.hash = '/pricing'} className="btn-primary w-full mt-4 !py-2 text-xs">
                  Upgrade to Pro
                </button>
              )}
            </div>
          )}

          <div className="panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
              <h2 className="caption flex items-center gap-2 !text-slate-800 dark:!text-white">
                <SafeIcon icon={FiFilter} className="text-blue-600" /> Filters
              </h2>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline">Clear all</button>
              )}
            </div>
            <div className="p-4 space-y-5">
              <div>
                <label className="caption block mb-2">Part status</label>
                <div className="border border-slate-300 dark:border-slate-700 divide-y divide-slate-200 dark:divide-slate-800">
                  {['All', 'Active', 'Discontinued'].map(status => (
                    <button key={status} onClick={() => handleFilterChange('status', status)} aria-pressed={filters.status === status} className={`w-full min-h-[44px] lg:min-h-0 flex items-center justify-between px-3 py-2 text-sm text-left ${filters.status === status ? 'bg-blue-600 text-white font-semibold' : 'bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
                      {status} {filters.status === status && <SafeIcon icon={FiCheck} />}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="f-brand" className="caption block mb-2">Brand</label>
                <select id="f-brand" value={filters.brand} onChange={(e) => handleFilterChange('brand', e.target.value)} className={selectClass}>
                  {brandOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="f-type" className="caption block mb-2">Equipment type</label>
                <select id="f-type" value={filters.applianceType} onChange={(e) => handleFilterChange('applianceType', e.target.value)} className={selectClass}>
                  {typeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <div className="order-1 lg:order-2 flex-1 min-w-0 space-y-4">
        {isFree && (
          <div className="lg:hidden flex items-center justify-between gap-3 border border-slate-300 border-l-[3px] border-l-amber-500 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900">
            <span className="text-slate-700 dark:text-slate-300">
              <span className="font-mono font-semibold">{remaining}</span> of 10 free searches left
            </span>
            {remaining <= 2 && (
              <button type="button" onClick={() => window.location.hash = '/pricing'} className="font-semibold text-blue-700 dark:text-blue-300 min-h-[44px] px-2">
                Upgrade
              </button>
            )}
          </div>
        )}
        <div className="sticky top-14 z-30 -mx-4 px-4 py-3 bg-slate-100 dark:bg-slate-950 sm:-mx-6 sm:px-6 lg:static lg:mx-0 lg:px-0 lg:py-0">
          <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 mb-2">
            <label htmlFor="part-search" className="caption block">{activeMode.label2}</label>
            <div role="group" aria-label="Search by" className="flex w-full sm:w-auto border border-slate-300 dark:border-slate-700 divide-x divide-slate-300 dark:divide-slate-700">
              {SEARCH_MODES.map(m => (
                <button key={m.value} type="button" onClick={() => setSearchMode(m.value)} aria-pressed={searchMode === m.value}
                  className={`flex-1 sm:flex-none min-h-[40px] sm:min-h-0 px-3 py-1 text-xs font-semibold ${searchMode === m.value ? 'bg-blue-600 text-white' : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="h-5 w-5 text-slate-500" />
            </div>
            <input
              id="part-search"
              type="text"
              className="block w-full pl-12 pr-12 py-3.5 lg:py-4 bg-white dark:bg-slate-900 border border-slate-400 dark:border-slate-700 border-b-2 border-b-blue-600 text-base sm:text-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 focus:border-blue-600 outline-none"
              placeholder={activeMode.placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} aria-label="Clear search" className="absolute inset-y-0 right-0 w-12 justify-center flex items-center text-slate-500 hover:text-slate-900 dark:hover:text-white">
                <SafeIcon icon={FiX} className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between" aria-live="polite">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            <span className="font-mono font-semibold text-slate-900 dark:text-white">{totalCount.toLocaleString()}{totalIsApprox ? '+' : ''}</span> matching parts
          </p>
          {loading && (
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 text-xs font-semibold">
              <SafeIcon icon={FiLoader} className="animate-spin" /> Searching catalog&hellip;
            </div>
          )}
        </div>

        {parts.length > 0 ? (
          <div role="table" aria-label="Search results" aria-busy={opening} className={`border border-slate-300 dark:border-slate-800 ${opening ? 'cursor-progress opacity-70' : ''}`}>
            <div role="row" className={`${PART_ROW_GRID} hidden md:grid gap-x-4 px-4 py-2 bg-navy border-l-[3px] border-l-transparent`}>
              {['OEM part no.', 'Description', 'Equipment', 'Cross-refs', 'Status', ''].map((h, i) => (
                <div key={i} role="columnheader" className="font-cond text-[11px] font-semibold uppercase tracking-wider text-slate-300 text-left">{h}</div>
              ))}
            </div>
            {parts.map(part => (
              <div key={part.id} onClick={() => openPart(part)} className="cursor-pointer">
                <PartCard part={part} onOpenNewer={openNewer} />
              </div>
            ))}
          </div>
        ) : !loading && (
          <div className="panel text-center py-20 px-6">
            <SafeIcon icon={FiTerminal} className="h-8 w-8 text-slate-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">No parts match this search</h3>
            <p className="text-sm text-slate-500">Dashes and spaces don’t matter, so check the digits, try the shorter base number, or clear a filter.</p>
          </div>
        )}
      </div>

      {selectedPart && <PartDetailPanel part={selectedPart} from={openedFrom} focusNumber={focusNumber} onClose={()=> setSelectedPart(null)} />}
      {showPaywall && <LimitReachedModal onClose={() => { setShowPaywall(false); setSearchQuery(''); }} />}
    </div>
  );
};

export default SearchDashboard;